import React, { useCallback, useMemo } from 'react'
import { find, get } from 'lodash'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import queryString from 'query-string'
import { withRouter } from 'react-router-dom'
import { IconGeo } from 'blocket-ui'
import {
  getSubRegionOptions,
  getRegionOptions,
  flattenLocations,
  texts,
  groupKeyValues,
} from 'utils/geo'
import { getGeoParams } from 'utils/parameters'
import StyledSelect from './StyledMultiSelect'
import MultiSelect from './MultiSelect'

const Wrapper = styled.div`
  display: flex;
`
const StyledIconGeo = styled(IconGeo)`
  pointer-events: none;
  position: relative;
  top: 12px;
  padding-left: 10px;
  flex-shrink: 0;
`
const Separator = styled.div`
  height: 24px;
  margin: 8px 8px 8px 12px;
  width: 0;
  border-left: 1px solid white;
  opacity: 0.25;
`

const getRegionLabel = (r, ag, regions) => {
  if (ag) {
    return texts.ADJACENT_MUNICIPALITIES
  }
  if (r && r.length > 1) {
    return texts.SEVERAL_REGIONS
  }
  if (r && r.length === 1) {
    return get(regions.find((region) => queryString.parse(`${region.value}`).r === r[0]), 'label')
  }
  return texts.ALL_SWEDEN
}

const getmunicipalitiesAndSubAreasLabel = (m, sa, subRegions) => {
  if ((m && m.length > 1) || (sa && sa.length > 1)) {
    return texts.SEVERAL_MUNICIPALITIES
  }

  if (sa && sa.length === 1 && m && m.length === 1) {
    return get(
      subRegions.find((subRegion) => queryString.parse(`${subRegion.value}`).sa === sa[0]),
      'label',
    )
  }
  if (m && m.length === 1 && sa && sa.length === 0) {
    return get(
      subRegions.find((subRegion) => {
        return queryString.parse(`${subRegion.value}`).m === m[0]
      }),
      'label',
    )
  }
  return texts.ALL_MUNICIPALITIES
}

const DoubleDropdown = ({ regionParameter, onChange, location }) => {
  const queryParams = queryString.parse(location.search)
  const getParams = getGeoParams(queryParams)
  const { r, ag } = queryParams
  const flattenedR = getParams.r
  const showSubRegions = flattenedR.length === 1 && !ag

  const regionsTopOptions = [
    {
      label: texts.ALL_SWEDEN,
      value: '',
      level: 1,
    },
    {
      label: texts.ADJACENT_MUNICIPALITIES,
      value: 'ag=1',
      level: 1,
    },
  ]
  const subRegionsTopOptions = [
    {
      label: texts.ALL_MUNICIPALITIES,
      value: queryString.stringify({ r }),
      level: 2,
    },
  ]

  const regions = getRegionOptions(regionParameter)
  let municipalitiesAndSubAreas = useMemo(() => getSubRegionOptions(regionParameter, flattenedR), [
    regionParameter,
    flattenedR,
  ])

  // If a municipality has subareas they should be stored in a new object to be rendered at the top
  municipalitiesAndSubAreas = municipalitiesAndSubAreas
    .map((subRegion) => {
      if (subRegion.children) {
        subRegionsTopOptions.push(subRegion)
      }
      return subRegion
    })
    .filter((subRegion) => !subRegion.children && subRegion.value.indexOf('&sa=') === -1)

  const regionLabel = getRegionLabel(flattenedR, ag, regions)
  const allSubRegions = flattenLocations(regionParameter)
    .filter((loc) => loc.value.r === flattenedR[0])
    .filter((loc) => loc.level > 1)
    .map(({ label, value, level }) => {
      return {
        label,
        value: queryString.stringify(value),
        level,
      }
    })

  const allOptions = [...regions, ...allSubRegions]

  const municipalitiesAndSubAreasLabel = getmunicipalitiesAndSubAreasLabel(
    getParams.m,
    getParams.sa,
    allSubRegions,
  )

  const handleRegionChange = useCallback(
    (selectedOptions) => {
      const selectedOptionsWithoutM = selectedOptions.filter(
        (option) => option.label !== texts.ALL_MUNICIPALITIES && option.label !== texts.ALL_SWEDEN,
      )
      const regionValues = selectedOptionsWithoutM.map((selectedOption) =>
        queryString.parse(selectedOption.value),
      )
      const rValues = groupKeyValues(regionValues, 'r')
      const regionValuesContainAgValue = find(regionValues, (value) => value.ag)
      if (regionValuesContainAgValue) {
        onChange({
          ...rValues,
          ag: '1',
        })
      } else {
        onChange(rValues)
      }
    },
    [onChange],
  )

  const handleSubRegionChange = useCallback(
    (selectedOptions) => {
      const secondLevelOptions = selectedOptions.filter((option) => option.level > 1)
      const muniValues = secondLevelOptions.map((selectedOption) =>
        queryString.parse(selectedOption.value),
      )
      const saValues = groupKeyValues(muniValues, 'sa')
      const mValues = groupKeyValues(muniValues, 'm')

      onChange({
        r: flattenedR,
        ...saValues,
        ...mValues,
      })
    },
    [flattenedR, onChange],
  )

  return (
    <Wrapper>
      <StyledSelect>
        <StyledIconGeo height="16px" width="16px" color="#ffffff" />
        <MultiSelect
          placeholder={regionLabel}
          ariaLabel={`Välj län, ${regionLabel} valt`}
          topOptions={regionsTopOptions}
          options={regions}
          handleConfirm={handleRegionChange}
          data-cy="geo-picker-region"
          regionParameter={regionParameter}
          allOptions={allOptions}
          allSubRegions={allSubRegions}
        />
        <Separator style={{ visibility: showSubRegions ? 'visible' : 'hidden' }} />
        {showSubRegions ? (
          <MultiSelect
            placeholder={municipalitiesAndSubAreasLabel}
            ariaLabel={`Välj kommun, ${municipalitiesAndSubAreasLabel} valt`}
            topOptions={subRegionsTopOptions}
            options={municipalitiesAndSubAreas}
            handleConfirm={handleSubRegionChange}
            data-cy="geo-picker-municipality"
            regionParameter={regionParameter}
            allOptions={allOptions}
            allSubRegions={allSubRegions}
          />
        ) : (
          <div style={{ width: '100%' }} />
        )}
      </StyledSelect>
    </Wrapper>
  )
}

DoubleDropdown.propTypes = {
  regionParameter: PropTypes.object.isRequired,
  onChange: PropTypes.func,
  location: PropTypes.object.isRequired,
}
export default withRouter(DoubleDropdown)
